import { useState, useEffect, useRef, useCallback } from 'react';
import * as pickerService from '../services/api/picker';
import Storage from '../utils/storage';

const CACHE_KEY = 'dashboard_data_cache';
const CACHE_TTL = 15 * 1000; // 15 seconds - short cache for real-time updates
const MIN_FETCH_INTERVAL = 10 * 1000; // Minimum 10 seconds between API calls

/**
 * Custom hook for fetching dashboard data with intelligent caching
 * Prevents API flooding by:
 * - Caching responses with TTL (3 minutes)
 * - Enforcing minimum time between API calls (30 seconds)
 * - Using cached data when available and fresh
 * - Supporting manual refresh
 * - Only making API calls when user is on dashboard
 */
export const useDashboardData = (options = {}) => {
  const { 
    autoRefresh = true, 
    refreshInterval = 5 * 60 * 1000, // 5 minutes
    enabled = true 
  } = options;

  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Track last fetch time to prevent rapid successive calls
  const lastFetchTimeRef = useRef(0);
  const isFetchingRef = useRef(false);
  const refreshIntervalRef = useRef(null);

  /**
   * Check if cached data is still valid
   */
  const isCacheValid = useCallback((cachedData) => {
    if (!cachedData || !cachedData.timestamp) return false;
    const age = Date.now() - cachedData.timestamp;
    return age < CACHE_TTL;
  }, []);

  /**
   * Check if enough time has passed since last fetch
   */
  const canFetch = useCallback(() => {
    const timeSinceLastFetch = Date.now() - lastFetchTimeRef.current;
    return timeSinceLastFetch >= MIN_FETCH_INTERVAL;
  }, []);

  /**
   * Load dashboard data from cache
   */
  const loadFromCache = useCallback(async () => {
    try {
      const cached = await Storage.getItem(CACHE_KEY);
      if (cached) {
        const parsedCache = JSON.parse(cached);
        if (isCacheValid(parsedCache)) {
          return parsedCache.data;
        }
      }
    } catch (err) {
      console.warn('Error loading dashboard cache:', err);
    }
    return null;
  }, [isCacheValid]);

  /**
   * Save dashboard data to cache
   */
  const saveToCache = useCallback(async (data) => {
    try {
      const cacheEntry = {
        data,
        timestamp: Date.now(),
      };
      await Storage.setItem(CACHE_KEY, JSON.stringify(cacheEntry));
    } catch (err) {
      console.warn('Error saving dashboard cache:', err);
    }
  }, []);

  /**
   * Fetch dashboard data from API
   */
  const fetchDashboardData = useCallback(async (forceRefresh = false) => {
    // Prevent concurrent fetches
    if (isFetchingRef.current && !forceRefresh) {
      return;
    }

    // For real-time updates, we'll still check cache but with shorter TTL
    // This allows showing cached data instantly while fetching fresh data in background
    if (!forceRefresh) {
      const cachedData = await loadFromCache();
      if (cachedData) {
        // Update UI with cached data immediately for instant display
        setDashboardData(cachedData);
        setIsLoading(false);
        setError(null);
        // Check cache freshness - get timestamp from cache entry
        try {
          const cached = await Storage.getItem(CACHE_KEY);
          if (cached) {
            const parsedCache = JSON.parse(cached);
            const cacheAge = Date.now() - (parsedCache.timestamp || 0);
            // If cache is still fresh (less than 10 seconds old), skip API call
            if (cacheAge < 10 * 1000) {
              lastFetchTimeRef.current = Date.now();
              return; // Cache is very fresh, skip API call
            }
          }
        } catch (err) {
          // If we can't check timestamp, continue to fetch
        }
        // Cache exists but is getting stale, continue to fetch fresh data
      }
    }

    // Check rate limiting (unless forcing refresh)
    if (!forceRefresh && !canFetch()) {
      const cachedData = await loadFromCache();
      if (cachedData) {
        setDashboardData(cachedData);
        setIsLoading(false);
        return;
      }
    }

    // No valid cache or force refresh - make API call
    isFetchingRef.current = true;
    setIsLoading(true);
    setError(null);
    lastFetchTimeRef.current = Date.now();

    try {
      const result = await pickerService.getPickerDashboard();

      if (result.success && result.data) {
        setDashboardData(result.data);
        setError(null);
        await saveToCache(result.data);
      } else {
        // Try to use cached data on API error
        const cachedData = await loadFromCache();
        if (cachedData) {
          setDashboardData(cachedData);
          console.warn('API error, using cached data:', result.message);
          setError(result.message); // Show error but use cache
        } else {
          setError(result.message || 'Failed to fetch dashboard data');
        }
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      
      // Try to use cached data on error
      const cachedData = await loadFromCache();
      if (cachedData) {
        setDashboardData(cachedData);
        console.warn('Using cached data due to error');
        setError(err.message || 'Failed to fetch dashboard data'); // Show error but use cache
      } else {
        setError(err.message || 'Failed to fetch dashboard data');
      }
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [canFetch, loadFromCache, saveToCache]);

  /**
   * Manual refresh function
   */
  const refresh = useCallback(() => {
    fetchDashboardData(true);
  }, [fetchDashboardData]);

  /**
   * Initial load and setup
   */
  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    // Load cached data immediately for instant UI
    loadFromCache().then((cachedData) => {
      if (cachedData) {
        setDashboardData(cachedData);
        setIsLoading(false);
      }
    });

    // Fetch fresh data (will use cache if valid)
    fetchDashboardData(false);

    // Set up auto-refresh if enabled
    if (autoRefresh && refreshInterval > 0) {
      refreshIntervalRef.current = setInterval(() => {
        fetchDashboardData(false);
      }, refreshInterval);
    }

    // Cleanup when component unmounts (user leaves dashboard)
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [enabled, autoRefresh, refreshInterval, fetchDashboardData, loadFromCache]);

  return {
    dashboardData,
    isLoading,
    error,
    refresh,
  };
};

